import { Camera, Intersection, Object3D, Quaternion, Vector3 } from "three";
import type { PlacementType } from "../api/types/Product";
import { SugarModel } from "../loader/SugarModel";
import type { PlanarGraphController } from "../shape/segment/PlanarGraphController";
import { projectPointOnPath } from "../shape/segment/axis/pathHelpers";
import { segmentInteriorLogicalEdgeIndex } from "../shape/segment/segmentInteriorEdge";
import { productPlacementMode } from "../core/scene/SurfaceAnchor";
import {
  placeTypeOfModelData,
  ROOM_WALL_HEIGHT_M,
} from "../shape/structural/StructuralCatalog";
import { Line } from "../shape/Line";
import { Polygon } from "../shape/Polygon";
import { Room } from "../shape/Scene";
import {
  getModelObject,
  getTopSelectableSugarModel,
  raycast,
  tryRaycastOnPlaneFromScreen,
} from "../physics/PhysicsUtil";

const FLOOR_UP = new Vector3(0, 1, 0);
const FLOOR_PLANE = { position: new Vector3(0, 0, 0), normal: FLOOR_UP };
const UP_FACE_MIN_DOT = 0.85;

const WALL_PLACE_TYPES: ReadonlySet<PlacementType> = new Set([
  "WALL",
  "WALLANDFLOORANDCEIL",
]);

const OBJECT_PLACE_TYPES: ReadonlySet<PlacementType> = new Set([
  "FLOORANDOBJECT",
  "WALLANDFLOORANDOBJECT",
]);

export type SurfaceHit = {
  point: Vector3;
  normal: Vector3;
  rotation?: Quaternion;
  segmentId?: string;
  supportModel?: SugarModel;
};

export type GrabRay = {
  event: MouseEvent | TouchEvent | DragEvent | { clientX: number; clientY: number };
  camera: Camera;
  dom: HTMLElement;
};

function supportsWallPlacement(placeType?: PlacementType | null): boolean {
  if (!placeType) return false;
  return (
    WALL_PLACE_TYPES.has(placeType) ||
    placeType === "FLOORANDWALL" ||
    placeType === "WALLANDFLOORANDOBJECT"
  );
}

function supportsObjectPlacement(placeType?: PlacementType | null): boolean {
  return !!placeType && OBJECT_PLACE_TYPES.has(placeType);
}

function supportsFloorPlacement(placeType?: PlacementType | null): boolean {
  if (!placeType) return true;
  if (placeType === "CEILING") return false;
  return !isWallOnlyPlacement(placeType);
}

export function isWallOnlyPlacement(placeType?: PlacementType | null): boolean {
  if (!placeType) return false;
  return WALL_PLACE_TYPES.has(placeType);
}

export function clampWallMountedY(y: number, heightM: number): number {
  const h = Math.max(0.05, heightM);
  const maxBottom = Math.max(0, ROOM_WALL_HEIGHT_M - h);
  return Math.min(maxBottom, Math.max(0, y));
}

function isRaycastWall(wall: Line): boolean {
  return wall?.visible! && !wall?.hidden && wall?.length > 0 && Boolean(wall?.normal);
}

function roomMeshesForPlacement(
  scene: Room,
  wallAllowed: boolean,
  floorAllowed: boolean,
): Object3D[] {
  const meshes: Object3D[] = [];
  scene.childs?.forEach((child) => {
    if (floorAllowed && child instanceof Polygon && child.root) {
      meshes.push(child.root);
    } else if (
      wallAllowed &&
      child instanceof Line &&
      isRaycastWall(child) &&
      child.raycastingTarget
    ) {
      meshes.push(child.raycastingTarget);
    }
  });
  return meshes;
}

function worldNormalFromHit(hit: Intersection): Vector3 {
  if (!hit.face) return FLOOR_UP.clone();
  const n = hit.face.normal.clone().transformDirection(hit.object.matrixWorld);
  return n.lengthSq() > 1e-8 ? n.normalize() : FLOOR_UP.clone();
}

function wallFromHit(hit: Intersection): Line | undefined {
  let node: Object3D | null = hit.object;
  while (node) {
    if (node.userData?.parent instanceof Line) return node.userData.parent;
    if (node.userData?.owner instanceof Line) return node.userData.owner;
    node = node.parent;
  }
  return undefined;
}

function peerModelTargets(scene: Room, dragged: SugarModel): Object3D[] {
  const targets: Object3D[] = [];
  scene.models?.forEach((child) => {
    if (child instanceof SugarModel && child !== dragged) targets.push(child);
  });
  return targets;
}

function objectTopFromHit(
  hit: Intersection,
  dragged: SugarModel,
): SurfaceHit | undefined {
  const owner = getTopSelectableSugarModel(hit.object);
  if (!owner || owner === dragged) return undefined;
  if (worldNormalFromHit(hit).y < UP_FACE_MIN_DOT) return undefined;
  return {
    point: hit.point.clone(),
    normal: FLOOR_UP.clone(),
    supportModel: owner,
  };
}

function isFloorPickHit(hit: Intersection): boolean {
  let node: Object3D | null = hit.object;
  while (node) {
    if (node.userData?.segmentId) return false;
    if (
      typeof node.name === "string" &&
      node.name.startsWith("interior_floor_3d_")
    ) {
      return true;
    }
    node = node.parent;
  }
  return false;
}

function isFloorHit(scene: Room, hit: Intersection): boolean {
  if (wallFromHit(hit)) return false;
  const polygon = scene.childs?.find((c) => c instanceof Polygon) as Polygon | undefined;
  if (polygon?.root) {
    let node: Object3D | null = hit.object;
    while (node) {
      if (node === polygon.root) return true;
      node = node.parent;
    }
  }
  return worldNormalFromHit(hit).y > 0.5;
}

function surfaceFromRayHit(
  scene: Room,
  hit: Intersection,
  wallAllowed: boolean,
  floorAllowed: boolean,
): SurfaceHit | undefined {
  if (isFloorHit(scene, hit)) {
    if (!floorAllowed) return undefined;
    return { point: hit.point.clone(), normal: worldNormalFromHit(hit) };
  }
  const wall = wallFromHit(hit);
  console.warn("wall", {
    wallAllowed,
    wall,
    isRaycastWall: wall && isRaycastWall(wall),
    wallNormal: wall?.normal,
  });
  if (wallAllowed && wall && isRaycastWall(wall) && wall.normal) {
    return {
      point: hit.point.clone(),
      normal: wall.normal.clone().normalize(),
      rotation: wall.rotation?.clone(),
    };
  }
  return undefined;
}

function fallbackSurface(anchor: Vector3): SurfaceHit {
  return { point: anchor.clone(), normal: FLOOR_UP.clone() };
}

function floorPlaneFromRay(ray: GrabRay): SurfaceHit | undefined {
  const point = tryRaycastOnPlaneFromScreen(
    ray.event as any,
    FLOOR_PLANE,
    ray.camera,
    ray.dom,
  );
  if (!point) return undefined;
  return { point, normal: FLOOR_UP.clone() };
}

/**
 * Floor-only grab/move surface (Configurator + RD floor fallback).
 * No Room / walls — Y=0 plane raycast.
 */
export function pickFloorGrabSurface(
  anchor: Vector3,
  ray?: GrabRay,
): SurfaceHit {
  if (ray) {
    const onFloor = floorPlaneFromRay(ray);
    if (onFloor) return onFloor;
  }
  return fallbackSurface(anchor);
}

/** Grab referans düzlemi — floor-only (CFG sahne taşıma). */
export function setupFloorDragPlane(
  plane: { position: Vector3; normal: Vector3 },
  anchor: Vector3,
  ray?: GrabRay,
): void {
  const surface = pickFloorGrabSurface(anchor, ray);
  plane.position.copy(surface.point);
  plane.normal.copy(surface.normal);
}

/** RD/CFG ortak: grab’daki model poz + yüzey kayması → aday world position. */
export function candidatePositionFromGrabSurface(
  selectedPosition: Vector3,
  plane: { position: Vector3; normal: Vector3 },
  surface: SurfaceHit,
): Vector3 {
  return selectedPosition
    .clone()
    .add(surface.point.clone().sub(plane.position));
}

/** Grab/move: raycast yalnız izinli mesh'lere; FLOOR tipi imleç duvardayken zemin düzlemi. */
export function pickGrabSurface(
  scene: Room,
  model: SugarModel,
  anchor: Vector3,
  ray?: GrabRay,
  segments?: PlanarGraphController,
): SurfaceHit {
  if (ray && segments) {
    const placeType = placeTypeOfModelData(model.data);
    const wallAllowed = supportsWallPlacement(placeType);
    const floorAllowed = supportsFloorPlacement(placeType);
    const objectAllowed = supportsObjectPlacement(placeType);
    if (productPlacementMode(placeType) === "floor-and-wall" || objectAllowed) {
      const targets: Object3D[] = [];
      if (wallAllowed || floorAllowed) targets.push(...segments.surfacePickTargets);
      if (objectAllowed) targets.push(...peerModelTargets(scene, model));
      const hits = raycast(
        ray.event as any,
        targets,
        ray.camera,
        ray.dom,
      );
      for (const hit of hits ?? []) {
        let node: Object3D | null = hit.object;
        while (node && !node.userData?.segmentId) node = node.parent;
        const segmentId = node?.userData?.segmentId as string | undefined;
        if (segmentId && wallAllowed) {
          const spec = segments.specs.find((item) => item.id === segmentId);
          if (!spec) continue;
          const projection = projectPointOnPath(spec.path, {
            x: hit.point.x,
            z: hit.point.z,
          });
          const side =
            segmentInteriorLogicalEdgeIndex(
              segments.planarGraph,
              segmentId,
            ) === 1
              ? 1
              : -1;
          const surfaceNormal = new Vector3(
            projection.normal.x * side,
            0,
            projection.normal.z * side,
          ).normalize();
          return {
            point: hit.point.clone(),
            normal: surfaceNormal,
            rotation: new Quaternion().setFromUnitVectors(
              new Vector3(0, 0, 1),
              surfaceNormal,
            ),
            segmentId,
          };
        }
        if (floorAllowed && isFloorPickHit(hit)) {
          return { point: hit.point.clone(), normal: FLOOR_UP.clone() };
        }
        if (objectAllowed) {
          const top = objectTopFromHit(hit, model);
          if (top) return top;
        }
      }
    }
    if (isWallOnlyPlacement(placeType)) {
      return fallbackSurface(anchor);
    }
    return floorPlaneFromRay(ray) ?? fallbackSurface(anchor);
  }
  const placeType = placeTypeOfModelData(model.data);
  const wallAllowed = supportsWallPlacement(placeType);
  const floorAllowed = supportsFloorPlacement(placeType);
  const objectAllowed = supportsObjectPlacement(placeType);

  if (ray) {
    const meshes = roomMeshesForPlacement(scene, wallAllowed, floorAllowed);
    if (objectAllowed) meshes.push(...peerModelTargets(scene, model));
    if (meshes.length > 0) {
      const hits = raycast(ray.event as any, meshes, ray.camera, ray.dom);
      for (const hit of hits ?? []) {
        if (objectAllowed) {
          const owner = getModelObject(hit.object);
          if (owner instanceof SugarModel) {
            const top = objectTopFromHit(hit, model);
            if (top) return top;
            continue;
          }
        }
        const surface = surfaceFromRayHit(scene, hit, wallAllowed, floorAllowed);
        if (surface) return surface;
      }
    }

    return pickFloorGrabSurface(anchor, ray);
  }

  return fallbackSurface(anchor);
}

/** Grab referans düzlemi: anchor sabit, normal seçilen yüzeyden. */
export function setupDragPlane(
  plane: { position: Vector3; normal: Vector3 },
  scene: Room,
  model: SugarModel,
  anchor: Vector3,
  ray?: GrabRay,
  segments?: PlanarGraphController,
): void {
  const surface = pickGrabSurface(scene, model, anchor, ray, segments);
  plane.position.copy(surface.point);
  plane.normal.copy(surface.normal);
}

/** Camera-facing vertical plane through `anchor` (for beam Y-only drag). */
export function verticalPlaneFacingCamera(
  anchor: Vector3,
  camera: Camera,
): { position: Vector3; normal: Vector3 } {
  const normal = new Vector3();
  camera.getWorldDirection(normal);
  normal.y = 0;
  if (normal.lengthSq() < 1e-8) normal.set(0, 0, 1);
  else normal.normalize();
  return { position: anchor.clone(), normal };
}

/**
 * Ray against a vertical plane through the beam (facing camera).
 * Used for Y-only elevation drag; XZ come from the grab pose.
 */
export function pickVerticalGrabSurface(
  anchor: Vector3,
  ray: GrabRay,
): SurfaceHit {
  const plane = verticalPlaneFacingCamera(anchor, ray.camera);
  const point = tryRaycastOnPlaneFromScreen(
    ray.event as any,
    plane,
    ray.camera,
    ray.dom,
  );
  if (point) return { point, normal: plane.normal };
  return { point: anchor.clone(), normal: plane.normal };
}

/** Grab referans düzlemi — dikey (kiriş kot sürükleme). */
export function setupVerticalDragPlane(
  plane: { position: Vector3; normal: Vector3 },
  anchor: Vector3,
  ray?: GrabRay,
): void {
  if (ray) {
    const surface = pickVerticalGrabSurface(anchor, ray);
    plane.position.copy(surface.point);
    plane.normal.copy(surface.normal);
    return;
  }
  plane.position.copy(anchor);
  plane.normal.set(0, 0, 1);
}
